import { createApp, watch } from 'vue'
import '@fontsource-variable/inter'
import '@fontsource-variable/fraunces'
import '@fontsource-variable/fraunces/wght-italic.css'
import '@fontsource-variable/vazirmatn'
import '@fontsource-variable/noto-sans-arabic'
import '@/assets/atmosphere.css'
import '@/style.css'
import App from '@/App.vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { setupI18n, availableLanguages } from '@/i18n'
import router from '@/router'
import { installStaleChunkRecovery } from '@/utils/staleChunkRecovery'
import { useSettingsStore } from '@/stores/settings'
import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import { preloadFallbackImages } from '@/utils/imageHelpers'
import { detectLanguageFromUrl } from '@/utils/langUtiles'
import { resolveInitialLanguage } from '@/utils/initialLanguage'
import { createHead } from '@vueuse/head'
import { useConsentStore } from '@/stores/consent'
import { usePlannerStore } from '@/stores/planner'
import { App as CapacitorApp } from '@capacitor/app'
import { requestFcmToken } from '@/firebase'
import { getOrCreateClientId } from '@/utils/clientId'
import { shouldClearSessionOnHttpError } from '@/utils/httpAuth'
import { captureAndStoreUtm, getStoredUtm } from '@/utils/utm'
import { hapticDirective } from '@/utils/hapticDirective'
import { setupAutoHaptic } from '@/utils/autoHaptic'
import { initializeRevenueCat, loginRevenueCatUser, isRevenueCatSupportedPlatform } from '@/services/revenuecat'
import { SENTRY_IGNORED_ERROR_PATTERNS, shouldIgnoreSentryEvent, shouldInitialiseSentry } from '@/utils/sentry'
import * as Sentry from '@sentry/vue'

preloadFallbackImages()
captureAndStoreUtm()

axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
axios.defaults.withCredentials = true

// Function to determine the app platform
function getAppPlatform () {
  if (window.Capacitor && window.Capacitor.isNativePlatform()) {
    // Return the specific platform (ios or android)
    return window.Capacitor.getPlatform()
  }
  return 'web'
}

axios.interceptors.request.use(config => {
  // Add platform parameter to all requests
  const platform = getAppPlatform()

  // Ensure params exists
  if (!config.params) config.params = {}
  config.params.platform = platform

  // Add persistent client id header
  const clientId = getOrCreateClientId()
  if (!config.headers) config.headers = {}
  // Use standard casing; Axios will normalize as needed
  config.headers['X-Client-Id'] = clientId

  // The browser is the only thing that knows the visitor's timezone. Cloudflare
  // supplies a country, not a zone, and plan dates and notification quiet hours
  // both need one. The server validates it before storing.
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (timezone) config.headers['X-Timezone'] = timezone
  } catch {
    // Intl unavailable or blocked: the server falls back to its own timezone.
  }

  // Add language header to all requests
  const settingsStore = useSettingsStore()
  if (settingsStore.language) {
    config.headers['X-Language'] = settingsStore.language
  }

  captureAndStoreUtm()

  const method = (config.method || 'get').toLowerCase()
  const utm = getStoredUtm()
  if (utm && Object.keys(utm).length > 0) {
    if (['post', 'put', 'patch'].includes(method)) {
      // Attach stored attribution to write requests as body fields.
      if (!config.data || typeof config.data !== 'object') {
        config.data = config.data ? { _payload: config.data } : {}
      }
      for (const [k, v] of Object.entries(utm)) {
        if (config.data[k] == null) {
          config.data[k] = v
        }
      }
    } else {
      // Attach to reads (GET/DELETE) as query params so browse-only sessions
      // that never POST still carry attribution to the session tracker.
      for (const [k, v] of Object.entries(utm)) {
        if (config.params[k] == null) {
          config.params[k] = v
        }
      }
    }
  }

  return config
}, error => {
  return Promise.reject(error)
})

// Add response interceptor: clear session only when the token/session is invalid (401, or 403 other than premium gate)
axios.interceptors.response.use(response => response, error => {
  const url = error.config?.url
  if (
    error.response &&
    shouldClearSessionOnHttpError(error) &&
    url &&
    url !== '/api/logout'
  ) {
    console.warn('🔒 Session invalid or forbidden, clearing auth state')
    const authStore = useAuthStore()
    authStore.logout()
  }
  return Promise.reject(error)
})

async function initializeLanguageSettings (settingsStore, app) {
  const initialLang = resolveInitialLanguage({
    urlLang: detectLanguageFromUrl(),
    storedLang: settingsStore.language,
    systemLang: navigator.language?.split('-')[0] ?? null,
    available: availableLanguages,
  })

  await setupI18n(app, initialLang)
  await settingsStore.setLanguage(initialLang)
}

function updateTheme (settingsStore) {
  const isDark = settingsStore.theme === 'dark' || 
    (!settingsStore.theme && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
  document.documentElement.classList.toggle('dark', isDark)
}

function isPlainObject (value) {
  if (!value || typeof value !== 'object') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

function redactSensitiveFields (value) {
  const sensitiveKeys = ['authorization', 'cookie', 'token', 'password', 'secret', 'key', 'email', 'phone']
  if (!value || typeof value !== 'object') return value

  if (Array.isArray(value)) {
    return value.map((entry) => redactSensitiveFields(entry))
  }

  if (!isPlainObject(value)) {
    return value
  }

  const next = { ...value }
  for (const [k, v] of Object.entries(next)) {
    const lowered = String(k).toLowerCase()
    if (sensitiveKeys.some((needle) => lowered.includes(needle))) {
      next[k] = '***redacted***'
      continue
    }
    next[k] = redactSensitiveFields(v)
  }
  return next
}

function sanitizeUrl (url) {
  if (!url || typeof url !== 'string') return url
  try {
    const parsed = new URL(url, window.location.origin)
    const redactedQueryKeys = new Set(['token', 'auth_token', 'code', 'key'])
    for (const key of redactedQueryKeys) {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, '***redacted***')
      }
    }
    return parsed.toString()
  } catch {
    return url
  }
}

/**
 * Request and send FCM token to backend
 * Works for both web and native platforms
 */
async function sendToken () {
  // Request FCM token - this will use the appropriate method based on platform
  const token = await requestFcmToken({ requestPermission: false })
  if (!token) return

  // Send token to backend API
  try {
    await axios.post('/api/store-fcm-token', { token })
  } catch (e) {
    console.error('Error storing FCM token:', e)
  }
}

function setupDeepLinkListener () {
  window.deepLinkProcessed = false

  const normalizeIncomingPath = (incomingUrl) => {
    if (!incomingUrl || typeof incomingUrl !== 'string') return null

    // Custom-scheme deep links, e.g. yumnia://auth/google/callback?token=...
    if (incomingUrl.startsWith('yumnia://')) {
      const path = incomingUrl.replace('yumnia://', '')
      return path.startsWith('/') ? path : `/${path}`
    }

    // Universal/App links, e.g. https://yumnia.com/payment/success?...
    try {
      const parsed = new URL(incomingUrl)
      const allowedHosts = new Set(['yumnia.com', 'www.yumnia.com'])
      if (!allowedHosts.has(parsed.host)) return null
      return `${parsed.pathname}${parsed.search}${parsed.hash}`
    } catch {
      return null
    }
  }

  const handleIncomingUrl = async (incomingUrl) => {
    const normalizedPath = normalizeIncomingPath(incomingUrl)
    if (!normalizedPath) return

    const [pathname, queryString = ''] = normalizedPath.split('?')
    const params = new URLSearchParams(queryString)

    try {
      if (pathname.includes('/auth/google/callback')) {
        const token = params.get('token')
        const isNewUser = params.get('newUser') === 'true'
        const redirectFromQuery = params.get('redirect')

        if (token) {
          const authStore = useAuthStore()

          if (authStore.isAuthenticated) {
            await authStore.logout()
          }

          try {
            let sessionRedirect = null
            try {
              sessionRedirect = sessionStorage.getItem('yumnia-oauth-return')
              if (sessionRedirect) {
                sessionStorage.removeItem('yumnia-oauth-return')
              }
            } catch {
              // ignore
            }

            const redirectUrl = redirectFromQuery || sessionRedirect || authStore.getOriginalUrl() || '/'

            // Wait for user data to be fetched before redirecting
            const user = await authStore.setToken(token)
            if (user) {
              if (isNewUser) {
                authStore.saveOriginalUrl(redirectUrl)
                router.push({ name: 'RegistrationConfirmation' })
              } else {
                router.push(redirectUrl)
              }
            } else {
              console.error('📱 Failed to authenticate user: No user data returned')
              router.push('/login?error=auth_failed')
            }
          } catch (error) {
            console.error('📱 Error authenticating user:', error)
            router.push('/login?error=auth_error')
          }
        } else {
          router.push('/login?error=no_token')
        }
      } else if (pathname.startsWith('/login')) {
        const error = params.get('error')

        if (error) {
          console.error('📱 OAuth login error:', error)
          router.push('/login?error=' + error)
        } else {
          router.push('/login')
        }
      } else {
        // Handle all app routes (including web return URLs like Stripe)
        console.log('📱 Navigating to path:', normalizedPath)
        router.push(normalizedPath)
      }
    } catch (error) {
      console.error('📱 Error processing deep link:', error)
    }
  }

  if (window.Capacitor && window.Capacitor.isNativePlatform()) {
    // Handles cold start from a deep link.
    CapacitorApp.getLaunchUrl().then(({ url }) => {
      if (url) {
        window.deepLinkProcessed = true
        handleIncomingUrl(url)
      }
    }).catch(() => {})

    // Handles warm starts / app resume deep links.
    CapacitorApp.addListener('appUrlOpen', async (data) => {
      window.deepLinkProcessed = true
      if (data?.url) handleIncomingUrl(data.url)
    })

    // Android hardware/gesture back button: navigate back or exit app.
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back()
      } else {
        CapacitorApp.exitApp()
      }
    })
  }
}

(async () => {
  const app = createApp(App)

  const sentryDsn = import.meta.env.VITE_SENTRY_DSN
  const appPlatform = getAppPlatform()
  const isNative = appPlatform !== 'web'
  if (shouldInitialiseSentry({
    dsn: sentryDsn,
    isProductionBuild: import.meta.env.PROD,
    hostname: window.location.hostname,
    isNative,
  })) {
    Sentry.init({
      app,
      dsn: sentryDsn,
      environment: 'production',
      release: import.meta.env.VITE_SENTRY_RELEASE,
      ignoreErrors: SENTRY_IGNORED_ERROR_PATTERNS,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
      ],
      tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || 0.05),
      sendDefaultPii: false,
      beforeSend (event, hint) {
        if (shouldIgnoreSentryEvent(event, hint)) return null
        if (event.request?.url) {
          event.request.url = sanitizeUrl(event.request.url)
        }
        if (event.request?.headers) {
          event.request.headers = redactSensitiveFields(event.request.headers)
        }
        if (event.request?.data) {
          event.request.data = redactSensitiveFields(event.request.data)
        }
        if (event.extra) {
          event.extra = redactSensitiveFields(event.extra)
        }
        return event
      },
      beforeBreadcrumb (breadcrumb) {
        const next = { ...breadcrumb }
        if (next.category === 'navigation' && next.data?.to) {
          next.data = { ...next.data, to: sanitizeUrl(String(next.data.to)) }
        }
        if (next.data) {
          next.data = redactSensitiveFields(next.data)
        }
        return next
      },
      initialScope: {
        tags: {
          app_platform: appPlatform,
          is_native: String(isNative),
          build_mode: import.meta.env.MODE,
        },
      },
    })
  }
  installStaleChunkRecovery(router)

  const pinia = createPinia()
  pinia.use(piniaPluginPersistedstate)
  app.use(pinia)

  const head = createHead()
  app.use(head)

  const auth = useAuthStore()
  const planner = usePlannerStore()
  auth.initialize()
  if (isRevenueCatSupportedPlatform()) {
    const configured = await initializeRevenueCat()
    if (configured && auth.user?.id) {
      await loginRevenueCatUser(auth.user.id)
    }
  }

  const settingsStore = useSettingsStore()
  let lastPlannerRefreshUserId = auth.user?.id || null
  
  // Set initial theme if not already set (e.g., first time app opens)
  if (!settingsStore.theme) {
    const systemTheme = (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) ? 'dark' : 'light'
    settingsStore.setTheme(systemTheme)
  }
  
  updateTheme(settingsStore)
  await initializeLanguageSettings(settingsStore, app)

  app.use(router)

  // Register haptic directive globally (for explicit use)
  app.directive('haptic', hapticDirective)
  
  // Setup automatic haptic feedback for primary buttons
  setupAutoHaptic()

  setupDeepLinkListener()

  const consentStore = useConsentStore()
  await consentStore.init()

  if (auth.isAuthenticated) sendToken()

  watch(() => auth.isAuthenticated, (isAuth) => {
    if (isAuth) {
      sendToken()
    } else {
      planner.resetPlannerSnapshot()
    }
  }, { immediate: false })

  watch(() => auth.user?.id, async (userId) => {
    if (lastPlannerRefreshUserId && userId && lastPlannerRefreshUserId !== userId) {
      planner.resetPlannerSnapshot()
    } else if (lastPlannerRefreshUserId && !userId) {
      planner.resetPlannerSnapshot()
    }
    lastPlannerRefreshUserId = userId || null

    if (!userId || !isRevenueCatSupportedPlatform()) return
    try {
      await loginRevenueCatUser(userId)
    } catch (error) {
      console.error('RevenueCat user login failed:', error)
    }
  }, { immediate: true })

  app.mount('#app')

  // Disarms the boot watchdog in index.html. That watchdog is inline precisely
  // because it has to survive this file never running, so the handshake has to
  // be a global rather than anything imported.
  window.__yumniaBooted = true
})()
